import { Accordion, Box, Button, Text, TextField } from '@streamelements/frontend-ui';
import {
  createOutputDestinationStreamSettingsProvider,
  Destination,
  DestinationMetadataApiClient,
  DestinationsStorageApiClient,
} from '../../services/DestinationsApiClient';
import { VideoCompositionInfo } from '../../types/obs';
import { providerIcons } from '../../components/ProviderIcon';
import { AspectRatio } from '../../components/MultiStreamingTargetList/AspectRatio';
import { KeyboardArrowUpIcon, PrimaryOutputIcon } from '../../components/Icons';
import {
  ComboBoxContent,
  ComboBoxLoadingIndicator,
  ComboBoxOption,
  ComboBoxRoot,
  ComboBoxTrigger,
} from '../../components/Combobox';
import { Controller, useFormContext } from 'react-hook-form';
import { FormValues } from '.';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
// import { YouTubeSchedulePicker } from './-YouTubeSchedulePicker';
import { YouTubePrivacyField } from './-YouTubePrivacyField';
import { UnauthenticatedError } from '../../utils/auth';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/Tooltip';
import { ensureOBSPresence } from '../../utils/ensureOBSPresence';
import { promisefy } from '../../utils/promisefy';
import { Card } from '../../components/Card';
import { PropsWithChildren } from 'react';
import { YouTubeStreamKeyField } from './-YouTubeStreamKeyField';
import { useSearch } from '@tanstack/react-router';
import { YouTubeBroadcastTitle } from './-YouTubeBroadcastTitle';
import { XBroadcastSourcesField } from './-XBroadcastSourcesField';
import { XChatAccessField } from './-XChatAccessField';

type Props = {
  destination: Destination;
  output: Destination['outputs'][number];
  compositions: Record<string, VideoCompositionInfo>;
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
};

const destinationsQueryOptions = ({
  authContextProvider,
  serviceEnvironment,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
}) => ({
  queryKey: ['destinations'],
  queryFn: async () => {
    await ensureOBSPresence();

    const destinationsApiClient = new DestinationsStorageApiClient({
      authContextProvider,
      serviceEnvironment,
    });

    const destinations = await destinationsApiClient.getDestinations();
    const outputs = await promisefy(window.host.getAllStreamingOutputs)();

    const result = Object.fromEntries(
      destinations.map((destination) => {
        return [destination.id, { ...destination, outputs: destination.outputs.map((output) => outputs[output.id]).filter((output) => !!output) }];
      })
    );

    return result as Record<string, Destination>;
  },
});

function TiktokInputDisabledTooltip({ disabled, children }: PropsWithChildren<{ disabled: boolean }>) {
  if (!disabled) {
    return children;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Box css={{ width: '100%', display: 'flex', flexDirection: 'column' }}>{children}</Box>
      </TooltipTrigger>
      <TooltipContent>
        <Text.Body variant="caption" css={{ color: '$textPrimary70', maxWidth: 220 }}>
          <strong>Tiktok</strong> stream title and category can only be set before the stream starts.
        </Text.Body>
      </TooltipContent>
    </Tooltip>
  );
}

export function OutputEntry(props: Props) {
  const queryClient = useQueryClient();
  const { mode } = useSearch({
    from: '/_authenticated/settings/_layout/broadcast-settings/',
  });
  const { output, destination: destinationProp, compositions, authContextProvider, serviceEnvironment } = props;
  const form = useFormContext<FormValues>();
  const [categorySearch = ''] = useDebounce(
    destinationProp.provider === 'youtube' ? 'q' : form.watch(`outputs.${output.id}.categorySearch`),
    500
  );

  // const { data: streamingStatus } = useSuspenseQuery(streamingStatusQueryOptions);

  const { data: destinations } = useQuery({
    ...destinationsQueryOptions({
      authContextProvider,
      serviceEnvironment,
    }),
  });

  const destination = destinations?.[destinationProp.id];

  const categories = useQuery({
    queryKey: ['categories', destination?.provider === 'youtube' ? 'youtube' : output.id, categorySearch],
    queryFn: async () => {
      let retries = 0;

      const { queryFn: getDestination } = destinationsQueryOptions({
        authContextProvider,
        serviceEnvironment,
      });

      async function getCategories() {
        if (retries > 3) {
          throw new Error(`Failed to fetch ${destination?.provider} categories`);
        }

        const streamRoomProvider = createOutputDestinationStreamSettingsProvider({
          authContextProvider,
          serviceEnvironment,
          destinationMetadata: (await getDestination())[destinationProp.id],
        });

        return streamRoomProvider.getCategories({ query: categorySearch });
      }

      const destinationMetadataApiClient = new DestinationMetadataApiClient({
        authContextProvider,
        serviceEnvironment,
        destinationMetadata: (await getDestination())[destinationProp.id],
      });

      while (retries < 3) {
        try {
          const categories = await getCategories();
          queryClient.refetchQueries(
            destinationsQueryOptions({
              authContextProvider,
              serviceEnvironment,
            })
          );

          return categories;
        } catch (error) {
          if (error instanceof UnauthenticatedError) {
            await destinationMetadataApiClient.refreshTokens();
            retries++;
            continue;
          }

          throw error;
        }
      }

      throw new Error('Failed to setup stream room');
    },
    retry: false,
    enabled: !!categorySearch,
    staleTime: 5 * 60 * 1000,
  });

  useQuery({
    queryKey: ['initialCategory', destination?.provider, output.id],
    queryFn: async () => {
      const { queryFn: getDestination } = destinationsQueryOptions({
        authContextProvider,
        serviceEnvironment,
      });

      const streamRoomProvider = createOutputDestinationStreamSettingsProvider({
        authContextProvider,
        serviceEnvironment,
        destinationMetadata: (await getDestination())[destinationProp.id],
      });

      const categoryId = form.getValues(`outputs.${output.id}.category`);

      if (!categoryId) {
        return null;
      }

      const result = await streamRoomProvider.getCategoryById({ categoryId }).catch(() => null);

      if (!result) return null;

      await queryClient.setQueryData(
        ['categories', destination?.provider === 'youtube' ? 'youtube' : output.id, categorySearch],
        () => {
          return new Map([[result.id, result.displayName]]);
        }
      );

      form.setValue(`outputs.${output.id}.category`, result.id);
      form.setValue(`outputs.${output.id}.categorySearch`, result.displayName);

      return result;
    },
    staleTime: 10 * 60 * 1000,
  });

  if (!destination) {
    return null;
  }

  const Icon = providerIcons[destination.provider];
  const {
    videoFrame: { width, height },
  } = compositions[output.videoCompositionId ?? 'default'];

  const { isPrimaryOutput } = output.auxiliaryData ?? ({} as never);

  const isTiktokDisabled = destination.provider === 'tiktok' && output.isActive;

  return (
    <Accordion.Item key={output.id} value={output.id} asChild>
      <Card>
        <Accordion.Header
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            transition: 'padding 0.2s ease-in-out',
            '&[data-state="open"]': {
              pb: '1rem',
            },
          }}
        >
          <Icon />
          <AspectRatio height={height} width={width} />

          {isPrimaryOutput && (
            <Tooltip>
              <TooltipTrigger asChild>
                <PrimaryOutputIcon css={{ color: '$textPrimary70' }} />
              </TooltipTrigger>

              <TooltipContent css={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Text.Body>Primary output</Text.Body>
                <Text.Body variant="caption" css={{ color: '$textPrimary50', maxWidth: 220 }}>
                  A primary output is an output that dictates where the main stream will be broadcasted to.
                </Text.Body>
              </TooltipContent>
            </Tooltip>
          )}

          <Text.Body weight="bold">{destination.displayName}</Text.Body>

          {output.isActive && (
            <Box as="svg" viewBox="0 0 24 24" css={{ fill: 'tomato', size: '0.5rem' }}>
              <circle cx="12" cy="12" r="12" />
            </Box>
          )}

          <Accordion.Trigger asChild>
            <Button
              iconButton
              color="neutral"
              variant="ghost"
              css={{
                ml: 'auto',
                svg: {
                  transition: 'transform 0.2s ease-in-out',
                  transform: 'rotate(180deg)',
                },
                '&[data-state="open"] svg': {
                  transform: 'rotate(0deg)',
                },
              }}
              style={{ display: destination.provider === 'custom' ? 'none' : undefined }}
            >
              <KeyboardArrowUpIcon />
            </Button>
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content css={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <input type="hidden" readOnly {...form.register(`outputs.${output.id}.id` as const)} />
          <input
            type="hidden"
            readOnly
            {...form.register(`outputs.${output.id}.destinationId` as const)}
            value={destination.id}
          />
          <input
            type="hidden"
            readOnly
            {...form.register(`outputs.${output.id}.provider` as const)}
            value={destination.provider}
          />

          {destination.provider === 'custom' && (
            <Text.Body variant="caption" css={{ color: '$textPrimary70' }}>
              Titles and categories can be set from SE.Live only for known platforms. Please configure the title and
              category for this output on the platform's website.
            </Text.Body>
          )}

          <Controller
            control={form.control}
            name={`outputs.${output.id}.title` as const}
            render={({ field, fieldState: { error } }) => {
              if (destination.provider === 'custom') {
                return <></>;
              }

              if (destination.provider === 'youtube') {
                return (
                  <Box css={{ mt: '0.5rem' }}>
                    <YouTubeBroadcastTitle
                      output={output}
                      destinationMetadata={destination as Destination<'youtube'>}
                    />
                  </Box>
                );
              }

              const ProviderTooltip = TiktokInputDisabledTooltip;

              return (
                <ProviderTooltip disabled={!!isTiktokDisabled}>
                  <TextField.Single
                    label="Stream title"
                    name={field.name}
                    value={field.value}
                    onChange={(event) => {
                      console.log(destination.provider, event);
                      field.onChange(event);
                    }}
                    error={!!error?.message}
                    css={{ mt: '0.5rem' }}
                    disabled={!!isTiktokDisabled}
                  >
                    {error?.message && (
                      <TextField.HelperText css={{ color: '$textError' }}>{error?.message}</TextField.HelperText>
                    )}
                  </TextField.Single>
                </ProviderTooltip>
              );
            }}
          />

          <Controller
            name={`outputs.${output.id}.category` as const}
            disabled={isTiktokDisabled}
            render={({ field }) => {
              if (destination.provider === 'x' || destination.provider === 'custom') {
                return <></>;
              }

              const ProviderTooltip = TiktokInputDisabledTooltip;

              return (
                <ProviderTooltip disabled={!!field.disabled}>
                  <ComboBoxRoot
                    value={[field.value ?? '']}
                    inputValue={categories.data?.get(field.value ?? '') ?? ''}
                    onValueChange={(value) => {
                      field.onChange({
                        target: {
                          name: field.name,
                          value: value[0],
                        },
                      });
                    }}
                  >
                    <ComboBoxTrigger label="Stream category" inputProps={{ readOnly: true }} disabled={field.disabled}>
                      {categories.isPending && <ComboBoxLoadingIndicator />}
                    </ComboBoxTrigger>
                    <ComboBoxContent>
                      {destination.provider === 'youtube' ? null : (
                        <TextField.Single
                          label="Search categories"
                          inputProps={{ autoFocus: true }}
                          {...form.register(`outputs.${output.id}.categorySearch`)}
                        />
                      )}
                      {Array.from(categories.data?.entries() ?? []).map(([id, label]) => (
                        <ComboBoxOption key={id} value={id}>
                          {label}
                        </ComboBoxOption>
                      ))}
                    </ComboBoxContent>
                  </ComboBoxRoot>
                </ProviderTooltip>
              );
            }}
          />

          {destination.provider === 'youtube' && mode === 'streams:start' && (
            <>
              <YouTubePrivacyField outputId={output.id} />
              <YouTubeStreamKeyField destinationMetadata={destination as Destination<'youtube'>} output={output} />
              {/* <YouTubeSchedulePicker outputId={output.id} /> */}
            </>
          )}

          {destination.provider === 'x' && mode === 'streams:start' && (
            <>
              <XBroadcastSourcesField
                destinationMetadata={destination as Destination<'x'>}
                output={output as Destination<'x'>['outputs'][number]}
              />
              <XChatAccessField outputId={output.id} />
            </>
          )}

          {isTiktokDisabled && (
            <Text.Body css={{ color: '$textAttention' }}>
              Cannot edit provider settings for TikTok while streaming.
            </Text.Body>
          )}
        </Accordion.Content>
      </Card>
    </Accordion.Item>
  );
}
